import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, GitMerge, Sparkles, Shield, Save, AlertTriangle } from "lucide-react";
import { MEDICAL_QUESTIONS } from "@/lib/medicalQuestionnaire";

/*  Interactive Merge Comparison Matrix
 *  --------------------------------------------------------------
 *  Route: /staff/brand/customers/merge?ids=A,B,C
 *  UX:
 *    - Left "Final Master Profile" column shows the live preview built
 *      from the operator's radio picks.
 *    - Every source profile gets its own column with a radio button per
 *      field. Clicking a radio picks THAT profile's value for the master.
 *    - The Non-Destructive Merge notice explains what is COMBINED
 *      automatically (orders, loyalty, medical history, photos).
 *    - "Merge Profiles" hits POST /customers/merge/interactive which
 *      archives (not deletes) the source rows, invalidates any live JWT
 *      sessions, and sums loyalty balances onto the master.
 */

// Fields the operator picks between — MUST mirror the backend
// `_INTERACTIVE_MERGE_IDENTITY_KEYS` allowlist so any pick is honoured.
const IDENTITY_FIELDS = [
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "whatsapp", label: "Phone / WhatsApp" },
  { key: "email", label: "Email (login)" },
  { key: "birthday", label: "Date of Birth" },
  { key: "gender", label: "Gender" },
  { key: "occupation", label: "Occupation" },
  { key: "nationality", label: "Nationality" },
];

// Ordered list of profile letters (A / B / C…) so the operator can refer to
// each column by a stable short label. Cap at 8 which is way more than any
// real-world duplicate cluster.
const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

function displayValue(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return String(v);
}

function fmtAnswer(mq, key) {
  const entry = (mq || {})[key];
  if (!entry) return { txt: "— (not asked)", empty: true };
  const answer = entry.answer;
  const note = (entry.note || "").trim();
  if (answer === "yes") return { txt: note ? `Yes · ${note}` : "Yes", empty: false };
  if (answer === "no") return { txt: "No", empty: false };
  return { txt: note ? `— · ${note}` : "— (not asked)", empty: !note };
}

export default function CustomerMerge() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ids = useMemo(
    () => (params.get("ids") || "").split(",").map((s) => s.trim()).filter(Boolean),
    [params]
  );
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);           // [{customer, summary}]
  const [primaryId, setPrimaryId] = useState("");         // which id wins on ties
  const [fieldChoices, setFieldChoices] = useState({});   // {field: cid}
  const [medicalChoices, setMedicalChoices] = useState({}); // {qkey: cid}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ids.length < 2) {
      toast.error("Select at least two customers to merge");
      navigate("/staff/brand/customers");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rs = await Promise.all(ids.map((cid) => staffApi.get(`/customers/${cid}`)));
        if (cancelled) return;
        const data = rs.map((r) => r.data);
        // Sort by created_at asc so the oldest record is Profile A and pre-
        // selected as the primary — history stays with the eldest identity.
        data.sort((a, b) => (a.customer.created_at || "").localeCompare(b.customer.created_at || ""));
        setProfiles(data);
        const first = data[0].customer.id;
        setPrimaryId(first);
        // Seed radio picks with the oldest profile's values so the operator
        // starts from a coherent baseline they can tweak field-by-field.
        const initial = {};
        for (const f of IDENTITY_FIELDS) initial[f.key] = first;
        setFieldChoices(initial);
        const seedMedical = {};
        for (const q of MEDICAL_QUESTIONS) {
          // Pick the first profile that has a real answer for this question
          // so operators start from a non-empty preview whenever possible.
          const donor = data.find((p) => {
            const mq = (p.customer.medical_questionnaire || {})[q.key];
            return mq && (mq.answer === "yes" || mq.answer === "no" || (mq.note || "").trim());
          });
          seedMedical[q.key] = donor ? donor.customer.id : first;
        }
        setMedicalChoices(seedMedical);
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/staff/brand/customers");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ids, navigate]);

  const master = useMemo(() => {
    // Build the live preview from the current picks.
    if (!profiles.length) return null;
    const byId = Object.fromEntries(profiles.map((p) => [p.customer.id, p.customer]));
    const out = {};
    for (const f of IDENTITY_FIELDS) {
      const donor = byId[fieldChoices[f.key] || primaryId];
      out[f.key] = donor ? donor[f.key] : "";
    }
    const mq = {};
    for (const q of MEDICAL_QUESTIONS) {
      const donor = byId[medicalChoices[q.key] || primaryId];
      const src = (donor && donor.medical_questionnaire) || {};
      if (src[q.key]) mq[q.key] = src[q.key];
    }
    out.medical_questionnaire = mq;
    return out;
  }, [profiles, fieldChoices, medicalChoices, primaryId]);

  const combinedLoyalty = useMemo(
    () => profiles.reduce((sum, p) => sum + (parseInt(p.customer.loyalty_points, 10) || 0), 0),
    [profiles]
  );
  const combinedOrders = useMemo(
    () => profiles.reduce((sum, p) => sum + ((p.orders || []).length || 0), 0),
    [profiles]
  );
  const combinedAppointments = useMemo(
    () => profiles.reduce((sum, p) => sum + ((p.appointments || []).length || 0), 0),
    [profiles]
  );
  const combinedSeries = useMemo(
    () => profiles.reduce((sum, p) => sum + ((p.summary?.series_active_count || 0)), 0),
    [profiles]
  );
  const combinedPrefs = useMemo(
    // Additive rule: every preference from every profile survives on the
    // master. We echo that same count here so the operator can see up front
    // exactly how many notes will land in the Guest Preferences tab.
    () => profiles.reduce((sum, p) => sum + ((p.customer?.guest_preferences || []).length), 0),
    [profiles]
  );

  const setField = (key, cid) => setFieldChoices((s) => ({ ...s, [key]: cid }));
  const setMedical = (key, cid) => setMedicalChoices((s) => ({ ...s, [key]: cid }));

  const finalise = async () => {
    if (!primaryId) return;
    const mergeIds = profiles.map((p) => p.customer.id).filter((id) => id !== primaryId);
    if (!mergeIds.length) { toast.error("Nothing to merge"); return; }
    const primaryProfile = profiles.find((p) => p.customer.id === primaryId);
    const primaryName = primaryProfile?.customer.full_name || primaryProfile?.customer.email || primaryId;
    const confirm = window.confirm(
      `Merge ${profiles.length} profiles into "${primaryName}"?\n\n` +
      `• ${combinedOrders} orders, ${combinedAppointments} appointments and ${combinedLoyalty.toLocaleString()} loyalty points will be combined.\n` +
      `• The other ${mergeIds.length} profile${mergeIds.length === 1 ? "" : "s"} will be archived and their logins deactivated.\n\nThis cannot be undone.`
    );
    if (!confirm) return;
    setSaving(true);
    try {
      const r = await staffApi.post("/customers/merge/interactive", {
        primary_id: primaryId,
        merge_ids: mergeIds,
        field_choices: fieldChoices,
        medical_choices: medicalChoices,
      });
      toast.success(`Merged ${r.data.merged} profile${r.data.merged === 1 ? "" : "s"} · ${r.data.loyalty_points.toLocaleString()} pts on master`);
      navigate(`/staff/brand/customers/${primaryId}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  if (loading) return (
    <StaffShell context="brand">
      <div className="p-8 text-sm text-muted-foreground" data-testid="merge-loading">Loading profiles…</div>
    </StaffShell>
  );
  if (!profiles.length) return null;

  const cols = profiles.length;
  return (
    <StaffShell context="brand">
      <div className="mb-4">
        <button
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
          onClick={() => navigate("/staff/brand/customers?tab=duplicates")}
          data-testid="merge-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to duplicates
        </button>
        <p className="tracking-overline text-xs text-muted-foreground">CUSTOMERS · MERGE</p>
        <h1 className="text-3xl font-semibold mt-1 flex items-center gap-2" data-testid="merge-title">
          <GitMerge className="w-6 h-6" /> Merge Comparison
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pick the surviving value for every field. History, orders, photos, medical records and loyalty points are combined automatically — nothing is deleted.
        </p>
      </div>

      {/* Non-destructive summary strip */}
      <Card className="rounded-lg mb-4 border-emerald-200 bg-emerald-50" data-testid="merge-summary-card">
        <CardContent className="p-3 text-sm text-emerald-900 flex flex-wrap items-center gap-x-6 gap-y-1">
          <span className="inline-flex items-center gap-1.5 font-medium"><Shield className="w-4 h-4" /> Non-destructive combine</span>
          <span><strong>{combinedOrders}</strong> orders</span>
          <span><strong>{combinedAppointments}</strong> appointments</span>
          <span><strong>{combinedSeries}</strong> active series</span>
          <span><strong>{combinedLoyalty.toLocaleString()}</strong> loyalty pts</span>
          <span><strong>{combinedPrefs}</strong> guest preferences</span>
        </CardContent>
      </Card>

      <Card className="rounded-lg overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="merge-matrix">
              <thead className="bg-secondary/40 sticky top-0 z-10">
                <tr className="border-b border-border">
                  <th className="text-left px-3 py-2 w-[180px] font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">Field</th>
                  <th className="text-left px-3 py-2 w-[260px] bg-primary/10 border-x border-primary/20">
                    <div className="flex items-center gap-1.5 font-semibold text-primary">
                      <Sparkles className="w-3.5 h-3.5" /> Final Master Profile
                    </div>
                    <div className="text-[11px] text-muted-foreground font-normal mt-0.5">Live preview</div>
                  </th>
                  {profiles.map((p, i) => {
                    const isPrimary = p.customer.id === primaryId;
                    return (
                      <th key={p.customer.id} className={`text-left px-3 py-2 min-w-[220px] border-l border-border ${isPrimary ? "bg-amber-50" : ""}`} data-testid={`merge-col-header-${i}`}>
                        <div className="flex items-center gap-1.5 font-semibold">
                          <span>Profile {LETTERS[i] || i + 1}</span>
                          {isPrimary && <span className="inline-block px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] uppercase tracking-wider">Primary</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground font-normal mt-0.5 truncate">{p.customer.full_name || "—"}</div>
                        <div className="text-[10px] text-muted-foreground font-normal">Created {p.customer.created_at ? new Date(p.customer.created_at).toLocaleDateString() : "—"}</div>
                        {!isPrimary && (
                          <button
                            className="mt-1 text-[10px] text-primary hover:underline"
                            onClick={() => setPrimaryId(p.customer.id)}
                            data-testid={`merge-set-primary-${i}`}
                          >Make primary</button>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {/* --- Identity rows --- */}
                {IDENTITY_FIELDS.map((f) => (
                  <tr key={f.key} className="border-b border-border/60 hover:bg-secondary/20" data-testid={`merge-row-${f.key}`}>
                    <td className="px-3 py-2 text-xs font-medium text-muted-foreground">{f.label}</td>
                    <td className="px-3 py-2 bg-primary/5 border-x border-primary/20 font-medium" data-testid={`merge-master-${f.key}`}>
                      {displayValue(master?.[f.key])}
                    </td>
                    {profiles.map((p, i) => {
                      const val = p.customer[f.key];
                      const isEmpty = val == null || val === "";
                      const checked = fieldChoices[f.key] === p.customer.id;
                      return (
                        <td key={p.customer.id} className={`px-3 py-2 border-l border-border ${checked ? "bg-primary/10" : ""}`}>
                          <label className={`flex items-start gap-2 cursor-pointer ${isEmpty ? "opacity-60" : ""}`}>
                            <input
                              type="radio"
                              name={`field_${f.key}`}
                              className="mt-0.5 accent-primary"
                              checked={checked}
                              onChange={() => setField(f.key, p.customer.id)}
                              data-testid={`merge-radio-${f.key}-${i}`}
                            />
                            <span className="text-sm break-all">{displayValue(val)}</span>
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {/* --- Address block (single choice per profile) --- */}
                <tr className="border-b border-border/60 hover:bg-secondary/20" data-testid="merge-row-address">
                  <td className="px-3 py-2 text-xs font-medium text-muted-foreground">Address</td>
                  <td className="px-3 py-2 bg-primary/5 border-x border-primary/20 text-xs">
                    {[master?.street_1, master?.street_2, master?.city, master?.state, master?.postal_code].filter(Boolean).join(", ") || "—"}
                  </td>
                  {profiles.map((p, i) => {
                    const addr = [p.customer.street_1, p.customer.street_2, p.customer.city, p.customer.state, p.customer.postal_code].filter(Boolean).join(", ");
                    const checked = fieldChoices.street_1 === p.customer.id;
                    return (
                      <td key={p.customer.id} className={`px-3 py-2 border-l border-border ${checked ? "bg-primary/10" : ""}`}>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="field_address"
                            className="mt-0.5 accent-primary"
                            checked={checked}
                            onChange={() => {
                              const cid = p.customer.id;
                              setFieldChoices((s) => ({
                                ...s,
                                street_1: cid, street_2: cid, city: cid, state: cid, postal_code: cid,
                              }));
                            }}
                            data-testid={`merge-radio-address-${i}`}
                          />
                          <span className="text-xs break-words">{addr || "—"}</span>
                        </label>
                      </td>
                    );
                  })}
                </tr>

                {/* --- Medical Questionnaire divider --- */}
                <tr className="bg-secondary/60 border-y border-border">
                  <td colSpan={2 + cols} className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                    Pre-Treatment Medical Questionnaire
                  </td>
                </tr>

                {MEDICAL_QUESTIONS.map((q) => (
                  <tr key={q.key} className="border-b border-border/60 hover:bg-secondary/20" data-testid={`merge-medical-${q.key}`}>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{q.label}</td>
                    <td className="px-3 py-2 bg-primary/5 border-x border-primary/20 text-xs">
                      {fmtAnswer(master?.medical_questionnaire, q.key).txt}
                    </td>
                    {profiles.map((p, i) => {
                      const { txt, empty } = fmtAnswer(p.customer.medical_questionnaire, q.key);
                      const checked = medicalChoices[q.key] === p.customer.id;
                      return (
                        <td key={p.customer.id} className={`px-3 py-2 border-l border-border ${checked ? "bg-primary/10" : ""}`}>
                          <label className={`flex items-start gap-2 cursor-pointer ${empty ? "opacity-60" : ""}`}>
                            <input
                              type="radio"
                              name={`med_${q.key}`}
                              className="mt-0.5 accent-primary"
                              checked={checked}
                              onChange={() => setMedical(q.key, p.customer.id)}
                              data-testid={`merge-radio-med-${q.key}-${i}`}
                            />
                            <span className="text-xs">{txt}</span>
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {/* --- Combined-only rows (informational, no radios) --- */}
                <tr className="bg-secondary/60 border-y border-border">
                  <td colSpan={2 + cols} className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                    Non-Destructive Combine (auto)
                  </td>
                </tr>
                {[
                  { key: "loyalty_points", label: "Loyalty Points", accessor: (p) => `${(p.customer.loyalty_points || 0).toLocaleString()} pts` },
                  { key: "orders_count", label: "Orders / Invoices", accessor: (p) => `${(p.orders || []).length} orders` },
                  { key: "appts_count", label: "Appointments", accessor: (p) => `${(p.appointments || []).length} appts` },
                  { key: "series_count", label: "Active Series", accessor: (p) => `${p.summary?.series_active_count || 0} active` },
                  { key: "prefs_count", label: "Guest Preferences", accessor: (p) => `${(p.customer.guest_preferences || []).length} notes` },
                ].map((row) => (
                  <tr key={row.key} className="border-b border-border/60" data-testid={`merge-combine-${row.key}`}>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{row.label}</td>
                    <td className="px-3 py-2 bg-primary/5 border-x border-primary/20 text-xs font-medium text-emerald-800">
                      {row.key === "loyalty_points"   ? `${combinedLoyalty.toLocaleString()} pts (combined)` :
                       row.key === "orders_count"    ? `${combinedOrders} orders (combined)` :
                       row.key === "appts_count"     ? `${combinedAppointments} appts (combined)` :
                       row.key === "prefs_count"     ? `${combinedPrefs} notes (appended)` :
                                                       `${combinedSeries} active (combined)`}
                    </td>
                    {profiles.map((p) => (
                      <td key={p.customer.id} className="px-3 py-2 border-l border-border text-xs text-muted-foreground">
                        {row.accessor(p)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
          The <span className="font-medium">{profiles.length - 1}</span> non-primary profile{profiles.length - 1 === 1 ? "" : "s"} will be archived. Their emails become free to re-use and any active sessions are deactivated.
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate("/staff/brand/customers?tab=duplicates")} data-testid="merge-cancel-btn">Cancel</Button>
          <Button onClick={finalise} disabled={saving} className="gap-1.5" data-testid="merge-finalise-btn">
            <Save className="w-4 h-4" /> {saving ? "Merging…" : "Merge Profiles"}
          </Button>
        </div>
      </div>
    </StaffShell>
  );
}
